import { EnvironmentProviders, InjectionToken, Provider, Type } from '@angular/core';
import { getModuleGroupByName, SOFTLINE_CONFIG_SETTINGS } from '@softline/application';
import {
  provideSoftAppsModule,
  withSoftAppsModuleInfo,
  withSoftAppsModuleRoutes,
} from '@softapps/core';
import { provideLocalhostPermissions } from '@softline/auth';
import { masterdataRoutes } from './masterdata.routes';
import MasterdataSearchSettingsComponent from './pages/masterdata-search-settings/masterdata-search-settings.component';
import {
  MASTERDATA_SEARCH_SETTINGS_DEFAULT,
  SOFTLINE_MODULE_MASTERDATA,
  SOFTLINE_PERMISSION_MASTERDATA,
  SOFTLINE_SETTINGS_MASTERDATA_SEARCH,
} from './masterdata.shared';
import { MasterdataEntityDefinition } from './types';

export const SOFTLINE_CONFIG_MASTERDATA_ENTITIES = new InjectionToken<
  MasterdataEntityDefinition<any>[][]
>('SOFTLINE_CONFIG_MASTERDATA_ENTITIES');

export function provideMasterdataEntities(
  ...definitions: MasterdataEntityDefinition<any>[]
): Provider {
  return {
    provide: SOFTLINE_CONFIG_MASTERDATA_ENTITIES,
    multi: true,
    useValue: definitions,
  };
}

/** A detail panel contributed to an entity's search from outside this lib —
    the entity definition stays free of the panel's imports. */
export interface MasterdataDetailPanel<TDto = unknown> {
  /** `MasterdataEntityDefinition.name` the panel belongs to. */
  entity: string;
  component: () => Type<unknown>;
  /** Maps the previewed row to the component's input bindings. */
  inputs?: (preview: TDto) => Record<string, unknown>;
}

export const SOFTLINE_CONFIG_MASTERDATA_DETAIL_PANELS = new InjectionToken<
  MasterdataDetailPanel<any>[][]
>('SOFTLINE_CONFIG_MASTERDATA_DETAIL_PANELS');

export function provideMasterdataDetailPanels(
  ...panels: MasterdataDetailPanel<any>[]
): Provider {
  return {
    provide: SOFTLINE_CONFIG_MASTERDATA_DETAIL_PANELS,
    multi: true,
    useValue: panels,
  };
}

export function provideMasterdataModule(): Array<EnvironmentProviders | Provider> {
  return [
    ...provideSoftAppsModule(
      {
        name: SOFTLINE_MODULE_MASTERDATA,
        title: 'Stammdatensuche Test',
        icon: 'fa-regular fa-database',
        route: '/masterdata',
        permission: SOFTLINE_PERMISSION_MASTERDATA,
        badge: 'docs',
        group: getModuleGroupByName('Stammdaten'),
      },
      withSoftAppsModuleRoutes({ app: masterdataRoutes }),
      withSoftAppsModuleInfo({
        library: '@softapps/core/masterdata',
        description:
          'Stammdatensuche über Artikel, Kunden und weitere Entitäten (Testseite).',
      })
    ),
    provideMasterdataSearchSettings(),
    ...provideLocalhostPermissions(SOFTLINE_PERMISSION_MASTERDATA),
  ];
}

function provideMasterdataSearchSettings(): Provider {
  return {
    provide: SOFTLINE_CONFIG_SETTINGS,
    multi: true,
    useValue: {
      key: SOFTLINE_SETTINGS_MASTERDATA_SEARCH,
      component: MasterdataSearchSettingsComponent,
      title: 'Stammdaten Suche',
      icon: 'fa-regular fa-magnifying-glass',
      default: MASTERDATA_SEARCH_SETTINGS_DEFAULT,
    },
  };
}
